'use client'
import { NavToSrv, useAppShellStore } from '@bitechsystems/btappshell'
import {
  Card,
  Group,
  Stack,
  Title,
  Text,
  Button,
  NavLink,
  Anchor,
} from '@mantine/core'
import React, { ReactElement } from 'react'
import { AiOutlineInfoCircle } from 'react-icons/ai'
import {
  FaArrowRight,
  FaCreditCard,
  FaExchangeAlt,
  FaGlobe,
  FaLifeRing,
  FaPhotoVideo,
  FaPlay,
  FaStar,
} from 'react-icons/fa'
import { shallow } from 'zustand/shallow'

interface FeatureProps {
  title: string
  description: string
  leftIcon: ReactElement
  href?: string
  linkTitle?: string
  hrefType?: 'button' | 'link'
  linkIcon?: ReactElement
  linkOnClick?: (e: any) => void
}

const Feature = (props: FeatureProps) => {
  return (
    <Group px={'xl'} py='xs' align='flex-start'>
      {props.leftIcon}
      <Stack justify='flex-start' spacing='xs' align='flex-start'>
        <Text weight={500} size={16} color='#1bbae1'>
          {props.title}
        </Text>

        <Text weight={400} size={14} color='dimmed'>
          {props.description}
        </Text>
        <>
          {props.hrefType == 'button' && props.href && (
            <Button
              compact
              size='xs'
              leftIcon={props.linkIcon ?? <AiOutlineInfoCircle />}
              variant='default'
              pr={'xs'}
              onClick={
                props.linkOnClick ??
                (() => {
                  NavToSrv(
                    props.href ?? `/#${props.title}`,
                    false,
                    undefined,
                    true
                  )
                })
              }
            >
              {props.linkTitle ?? 'More'}
            </Button>
          )}
          {(props.hrefType ?? 'link') == 'link' && props.href && (
            <Anchor
              target='_blank'
              href={props.linkOnClick ? undefined : props.href}
              onClick={
                props.linkOnClick ??
                (() => {
                  NavToSrv(
                    props.href ?? `/#${props.title}`,
                    false,
                    undefined,
                    true
                  )
                })
              }
            >
              {props.linkIcon} {props.linkTitle ?? 'More'}
            </Anchor>
          )}
        </>
      </Stack>
    </Group>
  )
}

export const NewFeatureCard = ({ theme, classes }: any) => {
  const [isDesktop] = useAppShellStore((state) => [state.isDesktop], shallow)

  return (
    <Card shadow={'xs'} mt={'lg'}>
      <Card.Section px={'xl'} py='xs' withBorder>
        <Group>
          <Title weight={600} order={5} color={theme?.cardHeaderColor}>
            New <strong>Feature Spotlights</strong>
          </Title>
        </Group>
      </Card.Section>

      <Card.Section p={'xs'}>
        <Stack justify={'flex-start'} spacing='xs'>
          <Feature
            title='NCR Debt Help System'
            description='Gain quick and easy access and use of the NCR Debt Help
                    System (DHS) via Simplicity'
            leftIcon={<FaCreditCard color='blue' />}
            linkTitle='Go to Training'
            href='https://live.bitechsystems.co.za/webhelp/simplicity/content/search.htm?q=DHS'
            linkOnClick={
              isDesktop
                ? () =>
                    console.log(
                      `cla:event|opentraining|simptraining?ncr-debthelp.html`
                    )
                : undefined
            }
          />
          <Feature
            title='Credit Provider Integration'
            description='Request and Receive obligation data directly from the Credit
            Provider'
            leftIcon={<FaExchangeAlt color='red' />}
            linkTitle='Watch Video'
            linkIcon={<FaPlay />}
            href='https://live.bitechsystems.co.za/webhelp/simplicity/content/search.htm?q=DHS'
            linkOnClick={
              isDesktop
                ? () =>
                    console.log(
                      `cla:event|openlinkextern|https://simplicity.web.za/videos/creditproviderdataintegration/creditproviderdataintegration.html`
                    )
                : undefined
            }
          />
          <Feature
            title='Web Based Interface'
            description=' The first phase of an ongoing project to migrate Simplicity
            to the Web'
            leftIcon={<FaGlobe color='blue' />}
            // linkTitle='Watch Video'
            // linkIcon={<FaPlay />}
          />

          <Feature
            title='Workflow Module'
            description='Setup pre-formatted documents, emails, SMS, call scripts,
            user tasks and more'
            leftIcon={<FaExchangeAlt color='red' />}
            // linkTitle='Watch Video'
            // linkIcon={<FaPlay />}
          />
        </Stack>
      </Card.Section>
    </Card>
  )
}

export const TrainingCard = ({ theme, classes }: any) => {
  const [isDesktop] = useAppShellStore((state) => [state.isDesktop], shallow)

  return (
    <Card shadow={'xs'} mt={'lg'}>
      <Card.Section px={'xl'} py='xs' withBorder>
        <Group>
          <Title weight={600} order={5} color={theme.cardHeaderColor}>
            Simplicity Training
          </Title>
        </Group>
      </Card.Section>

      <Card.Section p={'xs'}>
        <Stack justify={'flex-start'} spacing='xs'>
          <Feature
            title='Simplicity Training: Introduction'
            description='Get started on how to get easy training for Simplicity'
            leftIcon={<FaStar color='green' />}
            linkTitle='Watch Video'
            linkIcon={<FaPlay />}
            href='https://live.bitechsystems.co.za/webhelp/simplicity/content/simplicity-training-videos.html'
            linkOnClick={
              isDesktop
                ? () =>
                    console.log(
                      `cla:event|openlinkextern|https://live.bitechsystems.co.za/webhelp/simplicity/content/simplicity-training-videos.html`
                    )
                : undefined
            }
          />
          <Feature
            title='Simplicity Training'
            description='View all of our Simplicity training videos in the Simplicity
            Help'
            leftIcon={<FaPhotoVideo color='blue' />}
            linkTitle='Go to Training'
            linkIcon={<FaArrowRight />}
            href='https://live.bitechsystems.co.za/webhelp/simplicity/content/simplicity-training.html'
            linkOnClick={
              isDesktop
                ? () => console.log(`cla:event|opentraining|simptraining.html`)
                : undefined
            }
          />
          <Feature
            title='Getting Started Guide'
            description=' A course to familiarise yourself with the basics of
            Simplicity'
            leftIcon={<FaLifeRing color='orange' />}
            linkTitle='Go to guide'
            linkIcon={<FaArrowRight />}
            href='https://live.bitechsystems.co.za/webhelp/simplicity/content/gettingstarted.html'
            linkOnClick={
              isDesktop
                ? () => console.log(`cla:event|openhelp|GettingStarted.html`)
                : undefined
            }
          />

          <Feature
            title='F.A.Q.'
            description='Take a look at the most frequently asked questions about
            Simplicity'
            leftIcon={<FaLifeRing color='red' />}
            linkTitle='Go to F.A.Q.'
            linkIcon={<FaArrowRight />}
            href='https://live.bitechsystems.co.za/webhelp/simplicity/content/listoffaqs.html'
            linkOnClick={
              isDesktop
                ? () => console.log(`cla:event|openhelp|listoffaqs.html`)
                : undefined
            }
          />
        </Stack>
      </Card.Section>
    </Card>
  )
}
